import React, { useMemo, useEffect, useRef, useCallback } from 'react'
import { Input, Button } from 'reactstrap'

import { useCartRedux, useAuth, useUserRedux, useCartTotals } from '@hooks'
import { DINING_METHOD, PTS_TO_DOLLAR, CASH_UNIT_CENTS } from '@common'
import { calculateCartTotals, calculateMaxCreditToUse, calculateAdjustedCreditToUse } from '@utils'

const RedeemPoints = () => {
    // Hooks
    const { items, creditToUseCents, setCreditToUseCents, resetCredit, isComplimentaryOrder } = useCartRedux()
    const { memberData } = useAuth()
    const { diningMethod } = useUserRedux()
    const { promoDiscount } = useCartTotals()

    // Refs
    const prevItemsRef = useRef(items)
    const isFirstRenderRef = useRef(true)

    // Derived values from member data
    const availableCreditCents = memberData?.credit?.totalAvailableCents || 0
    const discountPct = memberData?.benefit?.discountPct || 0

    // Memoized calculations
    const totalsWithoutCredit = useMemo(
        () => calculateCartTotals(items, 0, discountPct, isComplimentaryOrder, promoDiscount),
        [items, discountPct, isComplimentaryOrder, promoDiscount]
    )

    // Only apply stripe minimum ($0.50) for Advance Takeaway orders
    const stripeMinimumCents = diningMethod?.value === DINING_METHOD.AdvanceTakeaway ? 50 : 0

    const maxCreditToUse = useMemo(
        () => calculateMaxCreditToUse(totalsWithoutCredit.finalAmount, availableCreditCents, stripeMinimumCents),
        [totalsWithoutCredit.finalAmount, availableCreditCents, stripeMinimumCents]
    )

    // Calculate the actual maximum credit user can use (allowing decimals)
    // This allows users to use all available credit up to order amount, even if it creates decimals
    const actualMaxCreditToUse = useMemo(() => {
        const orderAmountCents = Math.round(totalsWithoutCredit.finalAmount * 100)
        return Math.min(availableCreditCents, orderAmountCents)
    }, [totalsWithoutCredit.finalAmount, availableCreditCents])

    // Helper function to update credit with adjustment
    // Uses calculateAdjustedCreditToUse for normal cases, but allows using all available credit when user wants to
    const updateCredit = useCallback(
        desiredCreditCents => {
            // Clamp desired credit to actualMaxCreditToUse first
            const clampedDesiredCredit = Math.min(desiredCreditCents, actualMaxCreditToUse)

            const orderAmountCents = Math.round(totalsWithoutCredit.finalAmount * 100)

            // If user wants to use credit close to or at actualMaxCreditToUse, check if we should allow it
            // This allows users to use all available credit even if it creates decimals
            const threshold = actualMaxCreditToUse - CASH_UNIT_CENTS
            if (clampedDesiredCredit >= threshold && clampedDesiredCredit <= actualMaxCreditToUse) {
                const payableAmount = orderAmountCents - clampedDesiredCredit

                // Allow using desired credit if:
                // 1. No stripe minimum restriction (for non-Advance Takeaway orders)
                // 2. Order amount is already below stripe minimum (small orders)
                // 3. Payable amount would be 0 (free order)
                // 4. Payable amount would be >= stripe minimum (valid payment, only for Advance Takeaway)
                const isOrderBelowMinimum = stripeMinimumCents > 0 && orderAmountCents < stripeMinimumCents
                const isFreeOrder = payableAmount === 0
                const isPayableAmountValid = stripeMinimumCents === 0 || payableAmount >= stripeMinimumCents

                if (
                    payableAmount >= 0 &&
                    (stripeMinimumCents === 0 || isOrderBelowMinimum || isFreeOrder || isPayableAmountValid)
                ) {
                    // Allow using the desired credit amount (even if it creates decimals)
                    setCreditToUseCents(clampedDesiredCredit)
                    return
                }
            }

            // For normal cases, use the original calculation function to get adjusted credit
            const adjustedCredit = calculateAdjustedCreditToUse(
                totalsWithoutCredit.finalAmount,
                clampedDesiredCredit,
                availableCreditCents,
                stripeMinimumCents
            )
            setCreditToUseCents(adjustedCredit)
        },
        [totalsWithoutCredit.finalAmount, availableCreditCents, actualMaxCreditToUse, stripeMinimumCents, setCreditToUseCents]
    )

    // Calculate first increment credit based on order amount remainder
    const calculateFirstIncrementCredit = useCallback(() => {
        const orderAmountCents = Math.round(totalsWithoutCredit.finalAmount * 100)
        const remainder = orderAmountCents % CASH_UNIT_CENTS
        return remainder > 0 ? remainder : CASH_UNIT_CENTS
    }, [totalsWithoutCredit.finalAmount])

    // Event handlers
    // eslint-disable-next-line no-unused-vars
    const handlePointsChange = useCallback(
        e => {
            const value = e.target.value
            if (value === '' || value === '0') {
                setCreditToUseCents(0)
                return
            }
            const points = parseInt(value, 10) || 0
            updateCredit(points)
        },
        [setCreditToUseCents, updateCredit]
    )

    const handleDecrement = useCallback(() => {
        if (creditToUseCents <= 0) return
        const newCredit = Math.max(0, creditToUseCents - CASH_UNIT_CENTS)
        updateCredit(newCredit)
    }, [creditToUseCents, updateCredit])

    const handleIncrement = useCallback(() => {
        if (creditToUseCents === 0) {
            // First increment: use remainder or step size
            const firstIncrementCredit = calculateFirstIncrementCredit()
            updateCredit(firstIncrementCredit)
            return
        }
        // Subsequent increments: increase by step size, but cap at effectiveMaxCredit
        // Use actualMaxCreditToUse here to allow incrementing even for Advance Takeaway
        // The updateCredit function will handle the stripe minimum adjustment
        const newCredit = Math.min(creditToUseCents + CASH_UNIT_CENTS, actualMaxCreditToUse)
        if (newCredit > creditToUseCents) {
            updateCredit(newCredit)
        }
    }, [creditToUseCents, calculateFirstIncrementCredit, actualMaxCreditToUse, updateCredit])

    const handleUseMaximum = useCallback(() => {
        // For Advance Takeaway, use maxCreditToUse (with stripe minimum consideration)
        // For other orders, use actualMaxCreditToUse (allowing all available credit)
        const effectiveMaxCredit = diningMethod?.value === DINING_METHOD.AdvanceTakeaway ? maxCreditToUse : actualMaxCreditToUse
        updateCredit(effectiveMaxCredit)
    }, [actualMaxCreditToUse, maxCreditToUse, diningMethod, updateCredit])

    // Reset credit when cart items change
    useEffect(() => {
        // Skip on first render
        if (isFirstRenderRef.current) {
            isFirstRenderRef.current = false
            prevItemsRef.current = items
            return
        }

        // Check if items have actually changed (by comparing length and content)
        const itemsChanged =
            prevItemsRef.current.length !== items.length || JSON.stringify(prevItemsRef.current) !== JSON.stringify(items)

        if (itemsChanged && creditToUseCents > 0) {
            resetCredit()
        }

        // Update ref after checking
        prevItemsRef.current = items
    }, [items, creditToUseCents, resetCredit])

    // Reset credit when complimentary order is enabled
    useEffect(() => {
        if (isComplimentaryOrder && creditToUseCents > 0) {
            resetCredit()
        }
    }, [isComplimentaryOrder, creditToUseCents, resetCredit])

    // Calculate final amount after using points
    const finalAmountCents = useMemo(() => {
        return Math.round(totalsWithoutCredit.finalAmount * 100) - creditToUseCents
    }, [totalsWithoutCredit.finalAmount, creditToUseCents])

    // Check if should show PayNow minimum warning (for non-Advance Takeaway orders with points used)
    const shouldShowPayNowWarning = useMemo(() => {
        return (
            diningMethod?.value !== DINING_METHOD.AdvanceTakeaway &&
            creditToUseCents > 0 &&
            finalAmountCents > 0 &&
            finalAmountCents < 50
        )
    }, [diningMethod, creditToUseCents, finalAmountCents])

    // Early return: Only show if user is a member
    if (!memberData?.id) {
        return null
    }

    // Derived UI state
    const isDisabled = availableCreditCents < CASH_UNIT_CENTS || availableCreditCents <= 0 || isComplimentaryOrder
    // For Advance Takeaway, use maxCreditToUse (with stripe minimum consideration)
    // For other orders, use actualMaxCreditToUse (allowing all available credit)
    const effectiveMaxCredit = diningMethod?.value === DINING_METHOD.AdvanceTakeaway ? maxCreditToUse : actualMaxCreditToUse
    const isAtMaximum = creditToUseCents >= effectiveMaxCredit
    const isAtMinimum = creditToUseCents <= 0

    return (
        <div className="redeem-points-section">
            <div className="mb-3">
                <h6 className="redeem-points-title">Redeem Points for Discount</h6>
                <div className="d-flex justify-content-between mt-0">
                    <div className="d-flex flex-column mt-2">
                        <span className="redeem-points-available">Available: {availableCreditCents} pts</span>
                        {memberData?.credit?.expiringNextWeekCents > 0 && (
                            <span style={{ fontSize: '14px', color: '#ff9800' }}>
                                {memberData.credit.expiringNextWeekCents} pts expiring next week
                            </span>
                        )}
                    </div>
                </div>
                <div className="d-flex flex-row justify-content-end gap-2 mb-0 mt-3">
                    <Button
                        type="button"
                        outline
                        size="sm"
                        onClick={handleUseMaximum}
                        disabled={isDisabled || isAtMaximum}
                        className="redeem-points-maximum-btn"
                    >
                        Apply Max Points
                    </Button>
                    <Button
                        type="button"
                        outline
                        size="sm"
                        onClick={resetCredit}
                        disabled={isDisabled || isAtMinimum}
                        className="redeem-points-maximum-btn"
                    >
                        Clear
                    </Button>
                </div>
            </div>
            <div className="d-flex align-items-center justify-content-end gap-2 mb-2">
                <button
                    type="button"
                    className="redeem-points-btn-circle"
                    onClick={handleDecrement}
                    disabled={isDisabled || isAtMinimum}
                >
                    <i className="bx bx-minus"></i>
                </button>
                <div className="redeem-points-input-wrapper">
                    <Input
                        type="number"
                        min="0"
                        max={effectiveMaxCredit}
                        step={1}
                        value={creditToUseCents || ''}
                        readOnly
                        className="redeem-points-input"
                        disabled={isDisabled}
                    />
                </div>
                <button
                    type="button"
                    className="redeem-points-btn-circle"
                    onClick={handleIncrement}
                    disabled={isDisabled || isAtMaximum}
                >
                    <i className="bx bx-plus"></i>
                </button>
            </div>
            <p className="redeem-points-info">{PTS_TO_DOLLAR} pts = $1.00</p>
            {/* {diningMethod?.value === DINING_METHOD.AdvanceTakeaway && (
                <div className="redeem-points-note-box">Advance Takeaway requires minimum $0.50 total amount</div>
            )} */}
            {shouldShowPayNowWarning && (
                <div className="redeem-points-note-box">
                    If you want to pay via PayNow, you need a minimum of $0.50 total amount to use this payment method.
                </div>
            )}
        </div>
    )
}

export default RedeemPoints
